// Indicator maths for the chart window (owner 2026-10-13: "every indicator used in the app, settable, on one
// chart"). Same definitions as the engine (ai_engine/signal_generator.py): RSI / ATR / ADX use simple rolling
// means, Bollinger uses the rolling std, VWAP is cumulative per trading day. Every function returns an array
// aligned with the input bars (null where there is not enough data yet).

const sma = (vals, n) => {
  const out = new Array(vals.length).fill(null);
  let sum = 0;
  for (let i = 0; i < vals.length; i++) {
    sum += vals[i];
    if (i >= n) sum -= vals[i - n];
    if (i >= n - 1) out[i] = sum / n;
  }
  return out;
};

const ema = (vals, n) => {
  const out = new Array(vals.length).fill(null);
  const k = 2 / (n + 1);
  let e = null;
  for (let i = 0; i < vals.length; i++) {
    e = e === null ? vals[i] : vals[i] * k + e * (1 - k);
    out[i] = e;
  }
  return out;
};

const rollStd = (vals, n) => {
  const out = new Array(vals.length).fill(null);
  for (let i = n - 1; i < vals.length; i++) {
    let m = 0;
    for (let j = i - n + 1; j <= i; j++) m += vals[j];
    m /= n;
    let v = 0;
    for (let j = i - n + 1; j <= i; j++) v += (vals[j] - m) ** 2;
    out[i] = Math.sqrt(v / (n - 1 || 1));
  }
  return out;
};

const trueRange = (bars) => bars.map((b, i) => {
  if (i === 0) return b.high - b.low;
  const pc = bars[i - 1].close;
  return Math.max(b.high - b.low, Math.abs(b.high - pc), Math.abs(b.low - pc));
});

export const IND = {
  sma: { label: 'SMA', pane: false, params: { period: 20 }, color: '#c084fc',
    calc: (bars, p) => ({ main: sma(bars.map(b => b.close), +p.period) }) },
  ema: { label: 'EMA', pane: false, params: { period: 9 }, color: '#00d9ff',
    calc: (bars, p) => ({ main: ema(bars.map(b => b.close), +p.period) }) },
  bb: { label: 'Bollinger', pane: false, params: { period: 20, std: 2 }, color: '#888',
    calc: (bars, p) => {
      const c = bars.map(b => b.close);
      const m = sma(c, +p.period); const sd = rollStd(c, +p.period);
      return { mid: m, upper: m.map((x, i) => (x === null || sd[i] === null ? null : x + +p.std * sd[i])),
        lower: m.map((x, i) => (x === null || sd[i] === null ? null : x - +p.std * sd[i])) };
    } },
  vwap: { label: 'VWAP', pane: false, params: {}, color: '#ffb020',
    calc: (bars) => {
      let day = null, pv = 0, v = 0;
      return { main: bars.map(b => {
        const d = new Date(b.time * 1000).toISOString().slice(0, 10);
        if (d !== day) { day = d; pv = 0; v = 0; }
        const vol = b.volume || 1;
        pv += ((b.high + b.low + b.close) / 3) * vol; v += vol;
        return pv / v;
      }) };
    } },
  rsi: { label: 'RSI', pane: true, params: { period: 14, upper: 70, lower: 30 }, color: '#ffb020',
    calc: (bars, p) => {
      const n = +p.period; const c = bars.map(b => b.close);
      const g = c.map((x, i) => (i ? Math.max(0, x - c[i - 1]) : 0));
      const l = c.map((x, i) => (i ? Math.max(0, c[i - 1] - x) : 0));
      const ag = sma(g, n), al = sma(l, n);
      return { main: ag.map((x, i) => (x === null ? null : (al[i] === 0 ? 100 : 100 - 100 / (1 + x / al[i])))),
        levels: [+p.upper, +p.lower] };
    } },
  macd: { label: 'MACD', pane: true, params: { fast: 12, slow: 26, signal: 9 }, color: '#00d9ff',
    calc: (bars, p) => {
      const c = bars.map(b => b.close);
      const f = ema(c, +p.fast), s = ema(c, +p.slow);
      const m = f.map((x, i) => x - s[i]); const sig = ema(m, +p.signal);
      return { main: m, signal: sig, hist: m.map((x, i) => x - sig[i]) };
    } },
  stoch: { label: 'Stochastic', pane: true, params: { period: 14, smooth: 3, upper: 80, lower: 20 }, color: '#00ff88',
    calc: (bars, p) => {
      const n = +p.period;
      const k = bars.map((b, i) => {
        if (i < n - 1) return null;
        let hi = -Infinity, lo = Infinity;
        for (let j = i - n + 1; j <= i; j++) { hi = Math.max(hi, bars[j].high); lo = Math.min(lo, bars[j].low); }
        return hi === lo ? 50 : (100 * (b.close - lo)) / (hi - lo);
      });
      const d = sma(k.map(x => (x === null ? 0 : x)), +p.smooth).map((x, i) => (k[i] === null ? null : x));
      return { main: k, signal: d, levels: [+p.upper, +p.lower] };
    } },
  adx: { label: 'ADX', pane: true, params: { period: 14, level: 30 }, color: '#ff8844',
    calc: (bars, p) => {
      const n = +p.period; const tr = trueRange(bars);
      const pdm = bars.map((b, i) => { if (!i) return 0; const u = b.high - bars[i - 1].high, d = bars[i - 1].low - b.low; return u > d && u > 0 ? u : 0; });
      const mdm = bars.map((b, i) => { if (!i) return 0; const u = b.high - bars[i - 1].high, d = bars[i - 1].low - b.low; return d > u && d > 0 ? d : 0; });
      const atr = sma(tr, n), pd = sma(pdm, n), md = sma(mdm, n);
      const pdi = pd.map((x, i) => (x === null || !atr[i] ? null : (100 * x) / atr[i]));
      const mdi = md.map((x, i) => (x === null || !atr[i] ? null : (100 * x) / atr[i]));
      const dx = pdi.map((x, i) => (x === null || mdi[i] === null || x + mdi[i] === 0 ? 0 : (100 * Math.abs(x - mdi[i])) / (x + mdi[i])));
      const adx = sma(dx, n).map((x, i) => (pdi[i] === null ? null : x));
      return { main: adx, plus: pdi, minus: mdi, levels: [+p.level] };
    } },
  atr: { label: 'ATR', pane: true, params: { period: 14 }, color: '#c084fc',
    calc: (bars, p) => ({ main: sma(trueRange(bars), +p.period) }) },
  obv: { label: 'OBV', pane: true, params: {}, color: '#00d9ff',
    calc: (bars) => { let o = 0; return { main: bars.map((b, i) => { if (i) o += Math.sign(b.close - bars[i - 1].close) * (b.volume || 0); return o; }) }; } },
  volume: { label: 'Volume', pane: true, params: { ma: 20 }, color: '#888',
    calc: (bars, p) => ({ vol: bars.map(b => b.volume || 0), ma: sma(bars.map(b => b.volume || 0), +p.ma) }) },
};

// Classic Renko: same direction needs 1 brick, a reversal needs 2 (like the engine's ai_engine/renko.py)
export function renkoBricks(bars, brick, useHL = true) {
  // useHL: walk each bar's price path (up bar: open-low-high-close, down bar: open-high-low-close) so a
  // move inside a bar forms bricks like the bot's tick-by-tick engine does
  if (!bars.length || !(brick > 0)) return [];
  const out = [];
  let level = bars[0].open ?? bars[0].close, dir = 0, lastT = 0, pend = 0;
  // each brick carries the REAL volume traded since the previous brick (was 0 -> empty volume pane on Renko)
  const push = (o, c, t) => {
    const time = Math.max(t, lastT + 1); lastT = time;
    out.push({ time, open: o, close: c, high: Math.max(o, c), low: Math.min(o, c), volume: pend }); pend = 0;
  };
  const pts = [];
  for (const b of bars) {
    if (useHL && b.high != null && b.low != null) {
      const path = b.close >= b.open ? [b.open, b.low, b.high, b.close] : [b.open, b.high, b.low, b.close];
      path.forEach((p, k) => pts.push({ p, time: b.time, v: k === 0 ? (+b.volume || 0) : 0 }));
    } else pts.push({ p: b.close, time: b.time, v: +b.volume || 0 });
  }
  for (const { p, time: bt, v } of pts) {
    pend += v || 0;
    const b = { time: bt };
    for (let guard = 0; guard < 500; guard++) {
      if (dir >= 0 && p >= level + brick) { push(level, level + brick, b.time); level += brick; dir = 1; }
      else if (dir <= 0 && p <= level - brick) { push(level, level - brick, b.time); level -= brick; dir = -1; }
      else if (dir === 1 && p <= level - 2 * brick) { push(level - brick, level - 2 * brick, b.time); level -= 2 * brick; dir = -1; }
      else if (dir === -1 && p >= level + 2 * brick) { push(level + brick, level + 2 * brick, b.time); level += 2 * brick; dir = 1; }
      else break;
    }
  }
  return out;
}
